// src/components/ProjectOverview.jsx - Erweitert um Projektcontrolling
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { getProjectBudgets } from "../services/azureTableService";
import { useTheme } from "../contexts/ThemeContext";

const ProjectOverview = ({ contracts, employees, products, invoices }) => {
  const navigate = useNavigate();
  const { getComputedThemeValue, currentTheme } = useTheme();
  const [projectBudgets, setProjectBudgets] = useState({});
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [includeSent, setIncludeSent] = useState(false);

  const budgetBarColor = useMemo(() => {
    return getComputedThemeValue("--text-color");
  }, [getComputedThemeValue, currentTheme]);

  // Lade Budgets beim Komponenten-Mount
  useEffect(() => {
    const loadBudgets = async () => {
      const budgets = await getProjectBudgets();
      setProjectBudgets(budgets);
    };
    loadBudgets();
  }, []);

  // Dropdown-Handler
  const handleProjectToggle = (projectNumber) => {
    setSelectedProjects((prev) =>
      prev.includes(projectNumber)
        ? prev.filter((p) => p !== projectNumber)
        : [...prev, projectNumber]
    );
  };

  const selectAllProjects = () => {
    setSelectedProjects(projectData.map((p) => p.projectNumber));
  };

  const deselectAllProjects = () => {
    setSelectedProjects([]);
  };

  // Projektcontrolling Handler - navigiert zur Controlling-Ansicht
  const handleProjectControlling = (project) => {
    navigate(`/controlling/${project.projectNumber}`);
  };

  // Bestehende Logik für Projektdaten...
  const projectData = useMemo(() => {
    if (!contracts || contracts.length === 0) {
      return [];
    }

    const grouped = {};

    contracts.forEach((contract) => {
      if (!contract.reference) return;

      const projectNumber = contract.reference;

      // Filter für gesendete Dokumente
      if (
        !includeSent &&
        (contract.sentStatus === "SENT" || contract.sentStatus === "sent")
      ) {
        return;
      }

      if (!grouped[projectNumber]) {
        grouped[projectNumber] = {
          projectNumber,
          projectName: contract.displayTitle || "",
          totalAmount: 0,
          draftAmount: 0,
          sentAmount: 0,
          convertedAmount: 0,
          collectedAmount: 0,
          contracts: [],
          title: contract.title || "",
          receiverName: contract.customer || "Unbekannt",
        };
      }

      const amount = contract.documentTotal || 0;
      grouped[projectNumber].contracts.push(contract);
      grouped[projectNumber].totalAmount += amount;

      if (contract.status === "collected") {
        grouped[projectNumber].collectedAmount += amount;
      } else if (contract.status === "converted") {
        grouped[projectNumber].convertedAmount += amount;
      } else if (contract.status === "draft") {
        grouped[projectNumber].draftAmount += amount;
      } else if (
        contract.sentStatus === "SENT" ||
        contract.sentStatus === "sent"
      ) {
        grouped[projectNumber].sentAmount += amount;
      }

      if (grouped[projectNumber].contracts.length === 1) {
        grouped[projectNumber].title = contract.title || "";
        grouped[projectNumber].receiverName =
          contract.receiver?.name || "Unbekannt";
      }
    });

    return Object.values(grouped);
  }, [contracts, includeSent]);

  // Initiale Auswahl: Alle verfügbaren Projekte
  useEffect(() => {
    const allProjects = projectData.map((p) => p.projectNumber);
    setSelectedProjects(allProjects);
  }, [projectData]);

  // Gefilterte Daten für Chart und Tabelle
  const selectedProjectData = useMemo(() => {
    return projectData
      .filter((project) => selectedProjects.includes(project.projectNumber))
      .sort((a, b) => a.projectNumber.localeCompare(b.projectNumber));
  }, [projectData, selectedProjects]);

  // Chart-Daten mit Status-Kategorien
  const chartData = useMemo(() => {
    return selectedProjectData.map((project) => {
      const amounts = calculateInvoiceStats(project, invoices);
      const budget = projectBudgets[project.projectNumber] || 0;

      return {
        name: project.projectNumber,
        Bezahlt: amounts.paid,
        Fällig: amounts.due,
        Offen: amounts.open,
        Budget: budget, // Budget als separate Bar
      };
    });
  }, [selectedProjectData, invoices, projectBudgets]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("de-CH", {
      style: "currency",
      currency: "CHF",
    }).format(amount);
  };

  function calculateInvoiceStats(project, invoices) {
    const projectInvoices = invoices.filter(
      (inv) => inv.reference === project.projectNumber
    );
    const paid = projectInvoices
      .filter((inv) => inv.status === "paid")
      .reduce((sum, inv) => sum + inv.documentTotal, 0);

    const due = projectInvoices
      .filter((inv) => inv.status === "due")
      .reduce((sum, inv) => sum + inv.documentTotal, 0);

    const notBilled = project.totalAmount - (paid + due);
    return { paid, due, open: notBilled };
  }

  const getStatusLabel = (status) => {
    const statusMap = {
      draft: "Entwurf",
      sent: "Gesendet",
      converted: "Umgewandelt",
      collected: "Eingezogen",
    };
    return statusMap[status] || status;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, item) => sum + item.value, 0);
      return (
        <div className="tooltip">
          <p className="tooltip-title">{`Projekt ${label}`}</p>
          <p className="tooltip-total">{`Total: ${formatCurrency(total)}`}</p>
          {payload.map(
            (item, index) =>
              item.value > 0 && (
                <p
                  key={index}
                  className="tooltip-item"
                  style={{ color: item.color }}
                >
                  {`${getStatusLabel(item.dataKey)}: ${formatCurrency(
                    item.value
                  )}`}
                </p>
              )
          )}
        </div>
      );
    }
    return null;
  };

  const filteredData = selectedProjectData;

  return (
    <div className="project-overview">
      {/* Header mit Kontrollen */}
      <div className="project-header">
        <h2 className="project-title">Projektübersicht</h2>

        <div className="project-controls">
          {/* Projekt-Dropdown */}
          <div className="dropdown-container">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="dropdown-button"
            >
              <span>
                {selectedProjects.length === projectData.length
                  ? "Alle Projekte"
                  : selectedProjects.length === 0
                  ? "Keine Projekte"
                  : `${selectedProjects.length} Projekte`}
              </span>
              <svg
                className="dropdown-icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {dropdownOpen && (
              <div className="dropdown-menu">
                <div className="dropdown-header">
                  <button
                    onClick={selectAllProjects}
                    className="dropdown-action"
                  >
                    Alle
                  </button>
                  <button
                    onClick={deselectAllProjects}
                    className="dropdown-action"
                  >
                    Keine
                  </button>
                </div>
                <div className="dropdown-list">
                  {projectData.map((project) => (
                    <label
                      key={project.projectNumber}
                      className="dropdown-item"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProjects.includes(
                          project.projectNumber
                        )}
                        onChange={() =>
                          handleProjectToggle(project.projectNumber)
                        }
                        className="dropdown-checkbox"
                      />
                      <span>{project.projectNumber}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Gesendete Dokumente Option */}
          <label className="sent-option">
            <input
              type="checkbox"
              checked={includeSent}
              onChange={(e) => setIncludeSent(e.target.checked)}
              className="sent-checkbox"
            />
            <span>Gesendete Dokumente einbeziehen</span>
          </label>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              barCategoryGap="15%" // Noch weniger Abstand zwischen Gruppen
              barGap={1} // Minimaler Abstand zwischen den einzelnen Bars
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis
                tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend />

              <Bar dataKey="Bezahlt" stackId="main" fill="#10b981" />
              <Bar dataKey="Fällig" stackId="main" fill="#f59e0b" />
              <Bar dataKey="Offen" stackId="main" fill="#ef4444" />

              <Bar
                dataKey="Budget"
                fill={budgetBarColor}
                opacity={0.8}
                maxBarSize={6} // Nur 6px breit
                radius={[3, 3, 0, 0]} // Mehr Rundung für schmalen Bar
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tabelle */}
      <div className="table-container">
        <table className="project-table">
          <thead>
            <tr>
              <th>Projekt</th>
              <th>Bezahlt</th>
              <th>Fällig</th>
              <th>Offen</th>
              <th>Total</th>
              <th>Budget</th>
              <th>Controlling</th>
            </tr>
          </thead>
          <tbody>
            {filteredData.map((project, index) => {
              const amounts = calculateInvoiceStats(project, invoices || []);
              const budget = projectBudgets[project.projectNumber] || 0;

              return (
                <tr
                  key={project.projectNumber}
                  className={index % 2 === 0 ? "even" : "odd"}
                >
                  <td className="project-cell">
                    <span className="project-number">
                      {project.projectNumber}
                    </span>
                    {(project.projectName || project.title) && (
                      <span className="project-name">
                        {` - ${project.projectName || project.title}`}
                      </span>
                    )}
                  </td>
                  <td className="amount paid">
                    {formatCurrency(amounts.paid)}
                  </td>
                  <td className="amount due">{formatCurrency(amounts.due)}</td>
                  <td className="amount open">
                    {formatCurrency(amounts.open)}
                  </td>
                  <td className="amount total">
                    {formatCurrency(project.totalAmount)}
                  </td>
                  <td className="amount budget">
                    {budget > 0 ? formatCurrency(budget) : "-"}
                  </td>
                  <td className="controlling-cell">
                    <button
                      onClick={() => handleProjectControlling(project)}
                      className="btn btn-controlling"
                      title="Projektcontrolling öffnen"
                    >
                      <svg
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectOverview;
